import { useEffect, useState } from 'react'
import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  SubmitHandler,
  useForm,
  UseFormSetValue
} from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Loader,
  Select,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { MenuItem, Stack } from '@mui/material'
import { DataService } from 'api/DataService'
import { validateOrFail as validateOrFailCnpj } from 'validation-br/dist/cnpj'
import { validateOrFail as validateOrFailCpf } from 'validation-br/dist/cpf'
import * as yup from 'yup'

import InputController from 'components/InputController'
import { PhoneInput } from 'components/PhoneInput'

import { onlyNumber } from 'utils'
import { toastFirstError } from 'utils/yupResolverWithToast'

const typeOptions = [
  { label: 'Funcionário', value: 'employee' },
  { label: 'Fornecedor', value: 'supplier' },
  { label: 'Sócio', value: 'partner' },
  { label: 'Outro', value: 'other' }
]

export const schemaPayeeForm = yup.object({
  type: yup
    .string()
    .oneOf(typeOptions.map((type) => type.value))
    .required('O tipo é obrigatório'),
  name: yup.string().required('Nome é obrigatório'),
  phone: yup.string().nullable().notRequired(),
  email: yup
    .string()
    .nullable()
    .notRequired()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
      message: 'O e-mail informado contém caracteres inválidos.',
      excludeEmptyString: true
    }),
  document: yup
    .string()
    .nullable()
    .notRequired()
    .test('cpfCnpj', 'CPF/CNPJ inválido', function (value?: string | null) {
      const { path, createError } = this
      const digits = (value || '').replace(/\D/g, '')
      if (!digits) return true

      try {
        if (digits.length === 11) {
          validateOrFailCpf(digits)
        } else {
          validateOrFailCnpj(digits)
        }
        return true
      } catch (_e) {
        return createError({ message: 'CPF/CNPJ inválido', path })
      }
    }),
  notes: yup.string().nullable().notRequired()
})

export type PayeeFormSchema = yup.InferType<typeof schemaPayeeForm>

type PayeeFormProps<T extends FieldValues> = {
  setValueFromOutside?: UseFormSetValue<T>
  onCreated?: (item: { uuid: string; name: string }) => void
  uuid?: string
  handleClose: () => void
  defaultName?: string
}

const PayeeForm = <T extends FieldValues>({
  setValueFromOutside,
  onCreated,
  handleClose,
  uuid,
  defaultName
}: PayeeFormProps<T>) => {
  const [loading, setLoading] = useState(false)
  const { control, watch, handleSubmit, reset } = useForm<PayeeFormSchema>({
    defaultValues: { type: 'supplier', name: defaultName },
    resolver: yupResolver(schemaPayeeForm)
  })
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleClickToSubmit: SubmitHandler<PayeeFormSchema> = async () => {
    setDialogOpen(true)
  }

  const handleSubmitForm: SubmitHandler<PayeeFormSchema> = async (dataForm) => {
    setLoading(true)

    const trimmedEmail = dataForm.email?.trim() || ''
    const trimmedPhone = dataForm.phone?.trim() || ''
    const trimmedNotes = dataForm.notes?.trim() || ''
    const cleanedDocument = onlyNumber(dataForm.document || '')

    const payee: Record<string, any> = {
      name: dataForm.name,
      payee_type: dataForm.type
    }

    // Ao editar, campo apagado vai como null explícito (o back mantém o valor
    // antigo quando a chave não vem; '' não serve por causa da validação de e-mail).
    if (trimmedNotes) payee.notes = trimmedNotes
    else if (uuid) payee.notes = null
    if (trimmedEmail) payee.email = trimmedEmail
    else if (uuid) payee.email = null
    if (trimmedPhone) payee.phone = trimmedPhone
    else if (uuid) payee.phone = null
    if (cleanedDocument) payee.document = cleanedDocument
    else if (uuid) payee.document = null

    // O endpoint de beneficiário espera os campos aninhados em
    // `accounts_payable_payee` (ver o `params.fetch` do controller), ao
    // contrário de third_parties, que recebia tudo na raiz.
    const data = { accounts_payable_payee: payee }

    // O await é obrigatório: sem ele o setLoading(false) executa antes da resposta
    // e o botão reabilita com a requisição em voo (duplo clique = toast de erro ou duplicata).
    try {
      await toast.promise(
        async () => {
          const response = await DataService({
            data: data,
            type: uuid ? 'PATCH' : 'POST',
            url: uuid
              ? `/v1/accounts_payables/payees/${uuid}`
              : '/v1/accounts_payables/payees'
          })

          if (response.error) {
            setDialogOpen(false)
            throw new Error(
              response.error && (response.detail_error || response.message)
            )
          } else {
            // Com onCreated quem abriu o formulário decide onde gravar o item novo
            // (ex.: allocations.N.category); sem ele, grava no campo de raiz 'payee'.
            if (onCreated) {
              onCreated({
                uuid: response.data.record.uuid,
                name: response.data.record.name
              })
            } else if (setValueFromOutside) {
              setValueFromOutside(
                'payee' as Path<T>,
                {
                  label: response.data.record.name,
                  value: response.data.record.uuid
                } as PathValue<T, Path<T>>
              )
            }
            setDialogOpen(false)
            handleClose()
            return response.data.message
          }
        },
        {
          pending: `${uuid ? 'Atualizando' : 'Criando'} beneficiário`,
          success: {
            render({ data }) {
              return `${data}`
            }
          },
          error: {
            render({ data }) {
              return `${(data as any)?.message}`
            }
          }
        }
      )
    } catch {
      // erro já exibido pelo toast.promise
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!uuid) return

    const getPayee = async () => {
      setLoading(true)
      const response = await DataService({
        type: 'GET',
        url: `/v1/accounts_payables/payees/${uuid}`
      })

      const record = response.data.record

      reset({
        type: record.payee_type,
        name: record.name,
        phone: record.phone || '',
        email: record.email || '',
        document: record.document || '',
        notes: record.notes || ''
      })

      setLoading(false)
    }

    getPayee()
  }, [uuid])

  if (loading) {
    return <Loader />
  }

  return (
    <Stack justifyContent="space-between" flexGrow={1} gap="22px">
      <Stack gap="12px">
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              fullWidth={true}
              label="Tipo"
              placeholder="Selecione o tipo da conta"
              {...field}
            >
              {typeOptions.map(({ label, value }, index) => (
                <MenuItem key={index} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <InputController control={control} name="name">
          <Textfield
            label="Nome"
            placeholder={`Digite o nome do ${
              typeOptions
                .find((type) => type.value == watch('type'))
                ?.label.toLowerCase() || 'fornecedor'
            }`}
          />
        </InputController>

        <Controller
          control={control}
          name="phone"
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error }
          }) => (
            <PhoneInput
              country={'br'}
              errorLabel={error ? error.message : undefined}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange}
              label="Telefone"
            />
          )}
        />

        <InputController control={control} name="email">
          <Textfield label="E-mail" placeholder="Digite o e-mail" />
        </InputController>

        <InputController control={control} name="document">
          <Textfield
            label="CPF/CNPJ"
            mask="cpfCnpj"
            placeholder="Digite o CPF ou CNPJ (opcional)"
          />
        </InputController>

        <InputController control={control} name="notes">
          <Textfield label="Observações" placeholder="Ex: dados bancários" />
        </InputController>
      </Stack>

      <Stack direction="row" gap={1}>
        <Button fullWidth variantButton="neutral" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          fullWidth
          variantButton="primary"
          disabled={loading || !watch('name')?.length}
          onClick={handleSubmit(handleClickToSubmit, toastFirstError)}
        >
          {uuid ? 'Atualizar' : 'Salvar'}
        </Button>
      </Stack>

      <Dialog
        icon={uuid ? 'edit' : 'add'}
        title="Beneficiário"
        description={
          uuid
            ? 'Confirma atualizar esse Beneficiário?'
            : 'Confirma criar um Beneficiário?'
        }
        setIsOpenDialog={setDialogOpen}
        isOpenDialog={dialogOpen}
        cancelButton
      >
        <Button
          autoFocus
          disabled={loading}
          loading={loading}
          onClick={handleSubmit(handleSubmitForm, toastFirstError)}
          fullWidth
        >
          {uuid ? 'Atualizar' : 'Criar'}
        </Button>
      </Dialog>
    </Stack>
  )
}

export default PayeeForm
